import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import { AxiosError } from "axios";
import FroalaEditorView from "react-froala-wysiwyg/FroalaEditorView";

import { EditorViewStyles } from "@components/ReusableComponents/EditableContent/styles";

import { HandledError } from "@errors";
import { handleUploadFilesErrors } from "@errors/errors";

import { getCourseFiles, postCourseFile } from "@api/courses";
import {
  builtContentForPreview,
  builtContentForSave,
  getMinimalToolbarButtons,
  getToolbarButtons,
} from "@components/ReusableComponents/EditableContent/helpers";
import { useApplyTranslations } from "@hooks";
import { buildPaginatedSearchQuery, generalNotification } from "@utils/helpers";
import { getUnitSmartTags } from "@views/CourseEdit/api";

import {
  COURSE_FILES_DEFAULT_STATE,
  SMART_TAG_CLOSE,
  SMART_TAG_INSERT,
} from "@components/ReusableComponents/EditableContent/constants";
import queryKeys from "@constants/queryKeys";
import { URLS } from "@constants/urls";

import { Editor } from "@components";
import { useAIEditorOptions } from "@components/FormElements/Editor";
import SmartTagsModal from "@components/ReusableComponents/EditableContent/SmartTagsModal";

type EditableContentProps = {
  id: string;
  courseId: string;
  canViewCourseFiles: boolean;
  canUpdateContent: boolean;
  initialContent: string;
  viewOnlyContent?: string; // Used to show version history content
  placeholder?: string;
  canEdit: boolean;
  quickInsertEnabled?: boolean;
  showMinimalToolbarButtons?: boolean;
  isAIOptionsEnabled?: boolean;
  forceEnableWriteAbout?: boolean;
  allowImageUpload?: boolean;
  allowFilesUpload?: boolean;
  saveInterval?: number;
  onChange?: (newContent: string) => void;
  onContentSave?: (newContent: string) => void;
  onAIStatusChanged?: (isWorking: boolean) => void;
  onFocus?: () => void;
  onLinkImagePopupToggle?: (isOpen: boolean) => void;
};

const EditableContent: FC<EditableContentProps> = ({
  id,
  courseId,
  canViewCourseFiles,
  canUpdateContent,
  initialContent,
  viewOnlyContent,
  placeholder,
  canEdit,
  quickInsertEnabled = true,
  showMinimalToolbarButtons = false,
  isAIOptionsEnabled = false,
  forceEnableWriteAbout = false,
  allowImageUpload = true,
  allowFilesUpload = true,
  saveInterval,
  onChange,
  onContentSave,
  onAIStatusChanged,
  onFocus,
  onLinkImagePopupToggle,
}) => {
  const { t } = useApplyTranslations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const aiEditorOptions = useAIEditorOptions(isAIOptionsEnabled, forceEnableWriteAbout);

  const searchQuery = buildPaginatedSearchQuery(COURSE_FILES_DEFAULT_STATE);

  const { data: courseImagesRes } = useQuery(
    [queryKeys.courses.images, courseId],
    () => getCourseFiles(courseId, searchQuery),
    { enabled: canViewCourseFiles && allowFilesUpload },
  );

  const { data: smartTags = [] } = useQuery([queryKeys.courses.unitSmartTags], getUnitSmartTags, {
    select: (smartTagsRes) => smartTagsRes._data ?? [],
    enabled: canUpdateContent,
  });

  const courseImages = useMemo(() => courseImagesRes?._data ?? [], [courseImagesRes?._data]);

  const [isSmartTagsModalOpen, setSmartTagsModalOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState<string>(() =>
    builtContentForPreview(initialContent, courseImages),
  );
  const [saveContent, setSaveContent] = useState<string>(initialContent);
  const [isCodeViewActive, setIsCodeViewActive] = useState(false);

  const insertImagesOptions = useMemo(
    () => courseImages.reduce((obj, { url, name }) => ({ ...obj, [url]: name }), {}),
    [courseImages],
  );

  const smartTagsSelectOptions = useMemo(
    () => smartTags.map(({ value, label }) => ({ value, label })),
    [smartTags],
  );
  const hasSmartTags = smartTagsSelectOptions.length > 0;

  const toolbarButtons = useMemo(() => {
    return showMinimalToolbarButtons
      ? getMinimalToolbarButtons(aiEditorOptions)
      : getToolbarButtons(aiEditorOptions);
  }, [aiEditorOptions, showMinimalToolbarButtons]);

  const editorPlaceholder = placeholder ?? t("unitEdit.addContent");
  const hasViewOnlyContent = typeof viewOnlyContent === "string";
  const showEditorView = !canEdit || hasViewOnlyContent;

  const handleContentSave = useCallback(() => {
    onContentSave?.(saveContent);
  }, [onContentSave, saveContent]);

  const handleCodeViewChange = useCallback(
    (content: string) => {
      const newPreviewContent = builtContentForPreview(content, courseImages);
      setPreviewContent(newPreviewContent);
      setSaveContent(content);
      onChange?.(content);
    },
    [courseImages, onChange],
  );

  const handleTextViewChange = useCallback(
    (content: string) => {
      setPreviewContent(content);
      const newSaveContent = builtContentForSave(content, courseImages);
      setSaveContent(newSaveContent);
      onChange?.(newSaveContent);
    },
    [onChange, courseImages],
  );

  const { mutateAsync: postFileMutation } = useMutation(
    [queryKeys.courses.postFile, courseId],
    (file: File) => postCourseFile({ courseId: courseId, file: file }),
    {
      onSuccess: async () => {
        generalNotification("success", <p>{t("files.fileUploadSuccessful")}</p>);

        // Cancel course images queries to ensure that the invalidation will fetch the latest uploaded file
        queryClient.cancelQueries([queryKeys.courses.images, courseId]);
        await queryClient.invalidateQueries([queryKeys.courses.images, courseId]);
      },
      onError: (error: AxiosError) => {
        const handleError = (error: HandledError | null): void => {
          if (error?.id === "not_found.course_not_found") {
            navigate(URLS.courses.courses);
          }
        };
        handleUploadFilesErrors(error, false, handleError);
      },
    },
  );

  const handleInsertSmartTagClick = useCallback((): void => {
    setSmartTagsModalOpen(true);
  }, []);

  // Set preview content on image change
  useEffect(() => {
    if (courseImages.length > 0) {
      setPreviewContent(builtContentForPreview(initialContent, courseImages));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseImages]);

  useEffect(() => {
    if (initialContent !== saveContent) {
      setSaveContent(initialContent);
      setPreviewContent(builtContentForPreview(initialContent, courseImages));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContent]);

  // Do not render empty content when cannot edit
  if (!canEdit && !previewContent) return null;

  return (
    <section className="editable-content">
      {showEditorView ? (
        <div css={EditorViewStyles}>
          <FroalaEditorView
            model={
              hasViewOnlyContent
                ? builtContentForPreview(viewOnlyContent, courseImages)
                : previewContent
            }
          />
        </div>
      ) : (
        <>
          <Editor
            id={id}
            model={isCodeViewActive ? saveContent : previewContent}
            toolbarButtons={toolbarButtons}
            placeholderText={editorPlaceholder}
            toolbarInline
            minHeight={0}
            saveInterval={saveInterval}
            insertImagesOptions={allowFilesUpload ? insertImagesOptions : undefined}
            smartTagInsertSubscriber={SMART_TAG_INSERT}
            smartTagCloseSubscriber={SMART_TAG_CLOSE}
            quickInsertEnabled={quickInsertEnabled}
            aiEditorOptions={aiEditorOptions}
            onChange={isCodeViewActive ? handleCodeViewChange : handleTextViewChange}
            postImageMutation={allowImageUpload ? postFileMutation : undefined}
            onBlur={handleContentSave}
            onCodeViewToggle={setIsCodeViewActive}
            onInsertSmartTagButtonClick={handleInsertSmartTagClick}
            onAIStatusChanged={onAIStatusChanged}
            onFocus={onFocus}
            onLinkImagePopupToggle={onLinkImagePopupToggle}
          />

          {hasSmartTags && isSmartTagsModalOpen && (
            <SmartTagsModal
              isOpen={isSmartTagsModalOpen}
              options={smartTagsSelectOptions}
              onClose={(): void => setSmartTagsModalOpen(false)}
            />
          )}
        </>
      )}
    </section>
  );
};

export default React.memo(EditableContent);
